import { useState, useCallback, useEffect, useRef } from 'react'
import { TutorialStep, ExtendedHelpers } from '../../tour/types'

export interface SpotlightPosition {
  x: number
  y: number
  width: number
  height: number
}

export const useTourSpotlight = (
  run: boolean,
  isTourActive: boolean,
  joyrideSteps: any[],
  stepIndex: number,
  originalTutorialSteps: readonly TutorialStep[],
  createCompatibleExtendedHelpers: () => ExtendedHelpers | null
) => {
  const [spotlightPosition, setSpotlightPosition] = useState<SpotlightPosition>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  })
  const animationFrameRef = useRef<number>()

  const updateSpotlight = useCallback(async () => {
    if (!run || !joyrideSteps || stepIndex < 0 || stepIndex >= joyrideSteps.length) {
      if (spotlightPosition.width !== 0 || spotlightPosition.height !== 0) {
        setSpotlightPosition({ x: 0, y: 0, width: 0, height: 0 })
      }
      return
    }

    const currentStepDefInJoyride = joyrideSteps[stepIndex]
    const originalStepDef = originalTutorialSteps[stepIndex] as TutorialStep
    const targetSelector = currentStepDefInJoyride.target as string

    if (!targetSelector || targetSelector === 'body' || originalStepDef.isWelcomeScreen) {
      if (
        spotlightPosition.x !== 0 ||
        spotlightPosition.y !== 0 ||
        spotlightPosition.width !== 0 ||
        spotlightPosition.height !== 0
      ) {
        setSpotlightPosition({ x: 0, y: 0, width: 0, height: 0 })
      }
      return
    }

    try {
      const compatibleHelpers = createCompatibleExtendedHelpers()
      if (originalStepDef?.beforeShowPromise && compatibleHelpers) {
        await originalStepDef.beforeShowPromise(compatibleHelpers as ExtendedHelpers, stepIndex)
      }
      await new Promise((resolve) => setTimeout(resolve, 30))
      const targetElement = document.querySelector(targetSelector) as HTMLElement
      if (!targetElement) {
        if (spotlightPosition.width !== 0 || spotlightPosition.height !== 0) {
          setSpotlightPosition({ x: 0, y: 0, width: 0, height: 0 })
        }
        return
      }
      const rect = targetElement.getBoundingClientRect()
      const padding = 10
      const newSpotlightPos = {
        x: rect.left - padding,
        y: rect.top - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      }
      if (
        newSpotlightPos.x !== spotlightPosition.x ||
        newSpotlightPos.y !== spotlightPosition.y ||
        newSpotlightPos.width !== spotlightPosition.width ||
        newSpotlightPos.height !== spotlightPosition.height
      ) {
        setSpotlightPosition(newSpotlightPos)
      }
    } catch (error) {
      console.warn(
        `[Spotlight] Error in updateSpotlight for step ${stepIndex}, target ${targetSelector}:`,
        error
      )
      if (spotlightPosition.width !== 0 || spotlightPosition.height !== 0) {
        setSpotlightPosition({ x: 0, y: 0, width: 0, height: 0 })
      }
    }
  }, [
    run,
    stepIndex,
    joyrideSteps,
    originalTutorialSteps,
    createCompatibleExtendedHelpers,
    spotlightPosition,
  ])

  useEffect(() => {
    if (run && isTourActive) {
      animationFrameRef.current = requestAnimationFrame(() => {
        updateSpotlight()
      })
    } else if (!isTourActive && (spotlightPosition.width !== 0 || spotlightPosition.height !== 0)) {
      setSpotlightPosition({ x: 0, y: 0, width: 0, height: 0 })
    }
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [run, stepIndex, updateSpotlight, isTourActive, spotlightPosition])

  useEffect(() => {
    const handleResize = () => {
      if (run && isTourActive) {
        updateSpotlight()
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [run, updateSpotlight, isTourActive])

  return spotlightPosition
}
